/**
 * Searchable ⌘K rows from mock/API data. Wire new sources here — not in `command-menu-config.ts`.
 *
 * In this demo app the global ⌘K palette ships with route + Ask Leo entries only.
 * When a consumer adds a real data source (e.g. a Students hub), build a
 * `CommandMenuGroup` here with `searchOnly: true` so the palette stays
 * lightweight on open and the items only surface once the user types.
 */

import type { CommandMenuGroup } from "@/lib/command-menu-config"
import type { UserRole } from "@/contexts/exam-role-context"

export const COMMAND_MENU_SEARCH_DATA_GROUPS: CommandMenuGroup[] = []

export function getCommandMenuSearchDataGroups(role: UserRole): CommandMenuGroup[] {
  const groups: CommandMenuGroup[] = []

  // 1. Courses
  const courseItems = [
    {
      id: "course-cs101",
      label: "CS-101: Introduction to Computer Science",
      keywords: "cs101 computer science programming course",
      icon: "fa-light fa-book",
      href: "/course-offerings?search=CS-101",
    },
    {
      id: "course-bio202",
      label: "BIO-202: Organic Chemistry",
      keywords: "bio202 biology chemistry science course",
      icon: "fa-light fa-book",
      href: "/course-offerings?search=BIO-202",
    },
  ]

  // Math-301 is only visible to Admin
  if (role === "Admin") {
    courseItems.push({
      id: "course-math301",
      label: "MATH-301: Linear Algebra",
      keywords: "math301 mathematics algebra course",
      icon: "fa-light fa-book",
      href: "/course-offerings?search=MATH-301",
    })
  }

  groups.push({
    id: "courses",
    heading: `Courses (${courseItems.length})`,
    items: courseItems,
    searchOnly: true,
  })

  // 2. Assessments
  const assessmentItems = [
    {
      id: "assess-cs101-mid",
      label: "CS-101 Midterm Exam",
      keywords: "cs101 midterm exam assessment",
      icon: "fa-light fa-file-signature",
      href: "/assessments?search=CS-101",
    },
    {
      id: "assess-bio202-fin",
      label: "BIO-202 Final Examination",
      keywords: "bio202 final exam chemistry assessment",
      icon: "fa-light fa-file-signature",
      href: "/assessments?search=BIO-202",
    },
  ]

  if (role === "Admin") {
    assessmentItems.push({
      id: "assess-math301-quiz",
      label: "MATH-301 Pop Quiz",
      keywords: "math301 quiz math assessment",
      icon: "fa-light fa-file-signature",
      href: "/assessments?search=MATH-301",
    })
  }

  groups.push({
    id: "assessments",
    heading: `Assessments (${assessmentItems.length})`,
    items: assessmentItems,
    searchOnly: true,
  })

  // 3. Students (Access depends on RBAC)
  const studentItems = []

  if (role === "Admin") {
    studentItems.push(
      {
        id: "student-stu001",
        label: "John Doe (STU-001) - Enrolled in CS-101, BIO-202",
        keywords: "john doe stu001 student cs101 bio202",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=John",
      },
      {
        id: "student-stu002",
        label: "Jane Smith (STU-002) - Enrolled in BIO-202, MATH-301",
        keywords: "jane smith stu002 student bio202 math301",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=Jane",
      },
      {
        id: "student-stu003",
        label: "Alice Johnson (STU-003) - Enrolled in MATH-301",
        keywords: "alice johnson stu003 student math301",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=Alice",
      },
      {
        id: "student-stu004",
        label: "Bob Brown (STU-004) - Enrolled in CS-101",
        keywords: "bob brown stu004 student cs101",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=Bob",
      },
    )
  } else if (role === "Faculty") {
    // Faculty only teaches CS-101 and BIO-202, so they can see John Doe, Jane Smith, Bob Brown
    studentItems.push(
      {
        id: "student-stu001",
        label: "John Doe (STU-001) - Enrolled in CS-101, BIO-202",
        keywords: "john doe stu001 student cs101 bio202",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=John",
      },
      {
        id: "student-stu002",
        label: "Jane Smith (STU-002) - Enrolled in BIO-202",
        keywords: "jane smith stu002 student bio202",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=Jane",
      },
      {
        id: "student-stu004",
        label: "Bob Brown (STU-004) - Enrolled in CS-101",
        keywords: "bob brown stu004 student cs101",
        icon: "fa-light fa-graduation-cap",
        href: "/students?search=Bob",
      },
    )
  } else if (role === "Student") {
    // Student can only see their own record (John Doe STU-001)
    studentItems.push({
      id: "student-stu001",
      label: "John Doe (STU-001) - My Record",
      keywords: "john doe stu001 student my profile",
      icon: "fa-light fa-graduation-cap",
      href: "/reports?search=My",
    })
  }

  if (studentItems.length > 0) {
    groups.push({
      id: "students",
      heading: `Students (${studentItems.length})`,
      items: studentItems,
      searchOnly: true,
    })
  }

  return groups
}
